/**
 * Cosa si stampa di un drill: la **scheda dei bersagli** o la **piantina**.
 *
 * **Perché due fogli e non uno.** Chi va al campo vuole una cosa precisa: la
 * griglia T1, T2, … da compilare a penna, oppure il disegno dello stage da
 * appendere al palo. Il foglio unico con tutto dentro era «tutta la schermata
 * dell'esercizio», e in effetti stampava anche il resto della pagina.
 *
 * **Perché un portale su `body`.** La regola di stampa precedente nascondeva
 * «i figli di body che non contengono il foglio» — ma il foglio stava dentro
 * l'albero dell'app, quindi `#root` restava visibile con tutto quello che
 * conteneva. Il foglio ora è un figlio diretto di `body`, e la regola diventa
 * banale: nascondi tutto tranne lui. Nessun `:has()`, che su Safari vecchi
 * non c'è.
 *
 * **Perché `window.print()` e non un PDF lato server.** Zero dipendenze nuove,
 * zero carico sul backend, e la piantina è già nel DOM.
 *
 * La griglia vuota riusa la **stessa forma** della scheda a schermo (T1, T2, …
 * × A/C/D/M/NS): una sola struttura, su carta e su vetro.
 *
 * Spec: docs/shooting/13-piano-revisione-test-federico.md §Gruppo F ·
 *       15-seconda-sessione-federico.md
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Printer } from "lucide-react";
import { Button } from "../ui/button";
import { API_URL, fetchAPI } from "../../lib/utils";
import {
	START_CONDITION_FIREARM,
	START_CONDITION_SHOOTER,
	startConditionLabel,
} from "../../lib/programBuilderUtils";
import { StageShape } from "./StageShapes";
import { elementSpec, groupTargets, requiredShots } from "./stageElements";
import { scorableTargets } from "./TargetScoreCard";
import { plannedSeriesCount } from "./shootingPlan";

const PRINT_CLASS = "printing-stage-sheet";

/**
 * Stampa **solo** il foglio.
 *
 * Una classe sul `<body>` e il foglio come suo figlio diretto: in `@media
 * print` si nasconde ogni altro figlio di body. Aprire una finestra nuova
 * sarebbe più pulito in teoria, ma i popup vengono bloccati e il CSS
 * dell'app non la segue.
 */
const printSheet = () => {
	document.body.classList.add(PRINT_CLASS);
	const cleanup = () => {
		document.body.classList.remove(PRINT_CLASS);
		window.removeEventListener("afterprint", cleanup);
	};
	window.addEventListener("afterprint", cleanup);
	window.print();
	// Safari su iOS non emette sempre `afterprint`: una rete di sicurezza,
	// o la pagina resta in modalità stampa e l'app sembra sparita.
	setTimeout(cleanup, 2000);
};

const imageSrc = (url) =>
	!url ? null : url.startsWith("http") ? url : `${API_URL}${url}`;

/** La piantina disegnata, in bianco e nero, grande quanto il foglio. */
function StageDrawing({ prescription, maxHeight }) {
	const targets = prescription.targets || [];
	const layout = prescription.stage_layout || [];
	const widthM = prescription.stage_width_m || 20;
	const depthM = prescription.stage_depth_m || 25;
	const PX = 12;
	return (
		<svg
			viewBox={`0 0 ${widthM * PX} ${depthM * PX}`}
			className="w-full mb-3"
			style={{ maxHeight }}
			preserveAspectRatio="xMidYMid meet"
		>
			<rect width={widthM * PX} height={depthM * PX} fill="#fff" stroke="#999" />
			{[...layout, ...targets].map((item, index) => {
				const type = item.target_type || item.element_type;
				const spec = elementSpec(type);
				const magnify = spec.group === "target" ? 2.4 : 1;
				const w = (item.length_m || spec.widthM) * PX * magnify;
				const h = spec.heightM * PX * magnify;
				const x = ((item.x_m ?? 0) + widthM / 2) * PX;
				const y = (depthM - (item.y_m ?? 0)) * PX;
				return (
					<g
						key={index}
						transform={`translate(${x - w / 2}, ${
							spec.group === "target" ? y - h : y - h / 2
						})`}
					>
						<StageShape type={type} widthPx={w} heightPx={h} />
					</g>
				);
			})}
		</svg>
	);
}

/**
 * Il foglio. `mode`:
 *  - `sheet` — intestazione, dati di partenza e la **griglia di punteggio**;
 *  - `stage` — intestazione e la **piantina** (immagine o disegno) a tutta
 *    pagina, più gli altri file allegati (la foto del bersaglio).
 */
export function StagePrintSheet({
	name,
	prescription = {},
	zones = [],
	penalties = [],
	mode = "sheet",
}) {
	const targets = prescription.targets || [];
	const grouped = groupTargets(targets);
	const card = scorableTargets(targets);
	const layout = prescription.stage_layout || [];
	const hasDrawing = targets.some((t) => t.x_m != null) || layout.length > 0;
	const series = prescription.series || [];
	const seriesCount = plannedSeriesCount(prescription);
	// Senza serie previste si stampano tre righe vuote: al campo si spara
	// comunque, e un foglio senza righe da riempire non serve a niente.
	const rows =
		series.length > 0
			? series
			: Array.from({ length: Math.max(3, seriesCount) }, () => null);
	const perSeries = prescription.rounds_planned || requiredShots(targets);

	const facts = [
		prescription.firearm_type && [
			"Arma",
			{ handgun: "Pistola", rifle: "Carabina", shotgun: "Fucile", pcc: "PCC" }[
				prescription.firearm_type
			],
		],
		seriesCount > 0 && ["Serie", seriesCount],
		perSeries > 0 && [
			seriesCount > 1 ? "Colpi per serie" : "Colpi",
			seriesCount > 1 ? `${perSeries} (${perSeries * seriesCount} in totale)` : perSeries,
		],
		prescription.par_time_seconds && ["Par time", `${prescription.par_time_seconds}s`],
		(prescription.start_condition_firearm || prescription.start_position) && [
			"Arma alla partenza",
			startConditionLabel(
				START_CONDITION_FIREARM,
				prescription.start_condition_firearm || prescription.start_position,
			),
		],
		prescription.start_condition_shooter && [
			"Posizione",
			startConditionLabel(START_CONDITION_SHOOTER, prescription.start_condition_shooter),
		],
	].filter(Boolean);

	const goal = prescription.athlete_goal || prescription.success_criteria;
	const attachments = prescription.attachments || [];

	const sheet = (
		<div className={`${PRINT_CLASS}-root`}>
			<style>{`
				/* Il foglio è un figlio diretto di body: in stampa si nasconde
				   tutto il resto, senza :has(). Fuori dalla stampa non si vede. */
				.${PRINT_CLASS}-root { display: none; }
				@media print {
					body.${PRINT_CLASS} > *:not(.${PRINT_CLASS}-root) { display: none !important; }
					body.${PRINT_CLASS} > .${PRINT_CLASS}-root { display: block !important; }
					.${PRINT_CLASS}-root { color: #000; background: #fff; }
					.${PRINT_CLASS}-root table { page-break-inside: avoid; }
					@page { size: A4 portrait; margin: 12mm; }
				}
			`}</style>
			<div className="bg-white text-black p-6">
				<h1 className="text-xl font-bold mb-0.5">{name || "Drill di tiro"}</h1>
				<p className="text-xs text-neutral-600 mb-3">
					{prescription.mode === "dry_fire" ? "Dry fire" : "Live fire"}
					{grouped.length > 0 &&
						` · ${grouped
							.map(
								(t) =>
									`${t.count > 1 ? `${t.count}× ` : ""}${elementSpec(t.target_type).label}${
										t.distance_m ? ` @${t.distance_m}m` : ""
									}`,
							)
							.join(" · ")}`}
				</p>

				{mode === "stage" ? (
					<>
						{prescription.stage_image_url ? (
							<img
								src={imageSrc(prescription.stage_image_url)}
								alt="Piantina dello stage"
								className="w-full object-contain mb-3"
								style={{ maxHeight: "200mm" }}
							/>
						) : hasDrawing ? (
							<StageDrawing prescription={prescription} maxHeight="200mm" />
						) : null}
						{attachments.map((item, index) => (
							<figure key={`${item.url}_${index}`} className="mb-3">
								<img
									src={imageSrc(item.url)}
									alt={item.label || ""}
									className="w-full object-contain"
									style={{ maxHeight: "120mm" }}
								/>
								{item.label && (
									<figcaption className="text-xs text-neutral-600 mt-1">
										{item.label}
									</figcaption>
								)}
							</figure>
						))}
						{prescription.target_setup && (
							<p className="text-[11px] text-neutral-700 mt-2">
								Allestimento: {prescription.target_setup}
							</p>
						)}
					</>
				) : (
					<>
						{facts.length > 0 && (
							<table className="w-full text-xs mb-3 border-collapse">
								<tbody>
									{facts.map(([label, value]) => (
										<tr key={label} className="border-b border-neutral-300">
											<td className="py-1 pr-3 text-neutral-600 w-40">{label}</td>
											<td className="py-1 font-medium">{value}</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
						{goal && (
							<p className="text-xs mb-1">
								<span className="text-neutral-600">Obiettivo: </span>
								<span className="font-medium">{goal}</span>
							</p>
						)}
						{prescription.scoring_instructions && (
							<p className="text-xs mb-3 border border-black px-2 py-1">
								<span className="text-neutral-600">Da registrare: </span>
								<span className="font-medium">{prescription.scoring_instructions}</span>
							</p>
						)}

						{/* Griglia vuota: la stessa forma della scheda a schermo. */}
						{card.length > 0 && zones.length > 0 ? (
							<>
								<p className="text-[10px] uppercase tracking-wider text-neutral-600 mb-1">
									Punteggio
								</p>
								{rows.map((serie, index) => (
									<table
										key={index}
										className="w-full text-xs border-collapse mb-2"
										style={{ pageBreakInside: "avoid" }}
									>
										<thead>
											<tr>
												<th className="border border-neutral-400 p-1 text-left w-20 font-normal text-neutral-600">
													{serie?.label ||
														(serie?.distance_m
															? `${serie.distance_m} m`
															: `Serie ${index + 1}`)}
												</th>
												{zones.map((zone) => (
													<th
														key={zone.code}
														className="border border-neutral-400 p-1 font-medium"
													>
														{zone.code}
													</th>
												))}
												{penalties.map((penalty) => (
													<th
														key={penalty.code}
														className="border border-neutral-400 p-1 font-medium"
													>
														{penalty.code}
													</th>
												))}
												<th className="border border-neutral-400 p-1 font-medium w-16">
													Tempo
												</th>
											</tr>
										</thead>
										<tbody>
											{card.map((target) => (
												<tr key={target.target_no}>
													<td className="border border-neutral-400 p-1 text-neutral-600">
														T{target.target_no}
													</td>
													{[...zones, ...penalties].map((col) => (
														<td
															key={col.code}
															className="border border-neutral-400 h-7"
														/>
													))}
													<td className="border border-neutral-400" />
												</tr>
											))}
										</tbody>
									</table>
								))}
							</>
						) : (
							/* Senza bersagli numerati: una riga per serie, con le
							   colonne delle zone, da compilare a totale. */
							zones.length > 0 && (
								<table className="w-full text-xs border-collapse mb-2">
									<thead>
										<tr>
											<th className="border border-neutral-400 p-1 text-left w-20 font-normal text-neutral-600">
												Serie
											</th>
											{[...zones, ...penalties].map((col) => (
												<th
													key={col.code}
													className="border border-neutral-400 p-1 font-medium"
												>
													{col.code}
												</th>
											))}
											<th className="border border-neutral-400 p-1 font-medium w-16">
												Tempo
											</th>
										</tr>
									</thead>
									<tbody>
										{rows.map((serie, index) => (
											<tr key={index}>
												<td className="border border-neutral-400 p-1 text-neutral-600 h-7">
													{serie?.label ||
														(serie?.distance_m ? `${serie.distance_m} m` : index + 1)}
												</td>
												{[...zones, ...penalties].map((col) => (
													<td key={col.code} className="border border-neutral-400" />
												))}
												<td className="border border-neutral-400" />
											</tr>
										))}
									</tbody>
								</table>
							)
						)}
					</>
				)}
			</div>
		</div>
	);

	// Fuori dall'albero dell'app, come figlio diretto di body.
	return typeof document === "undefined" ? sheet : createPortal(sheet, document.body);
}

/**
 * I bottoni di stampa, con le zone caricate **al primo click**.
 *
 * La griglia vuota ha bisogno delle colonne del sistema di punteggio, che
 * stanno nei reference data. Caricarle al montaggio significherebbe una
 * chiamata per ogni esercizio di tiro che compare in lista, per un foglio che
 * quasi nessuno stampa: si caricano quando serve.
 *
 * Due bottoni, uno per foglio: compaiono solo se c'è qualcosa da stampare.
 */
export function StagePrintable({ name, prescription = {}, zones, penalties, t }) {
	const [loaded, setLoaded] = useState(
		zones ? { zones, penalties: penalties || [] } : null,
	);
	const [loading, setLoading] = useState(false);
	const [mode, setMode] = useState("sheet");

	const label = (key, fallback) => (typeof t === "function" ? t(key) : fallback);
	const targets = prescription.targets || [];
	const hasStage =
		!!prescription.stage_image_url ||
		(prescription.stage_layout || []).length > 0 ||
		targets.some((target) => target.x_m != null) ||
		(prescription.attachments || []).length > 0;
	const hasSheet =
		prescription.mode !== "dry_fire" &&
		(targets.length > 0 || !!prescription.scoring_system_id);

	const print = async (which) => {
		setMode(which);
		if (loaded) {
			requestAnimationFrame(() => requestAnimationFrame(printSheet));
			return;
		}
		setLoading(true);
		try {
			const reference = await fetchAPI("/shooting/reference").catch(() => ({}));
			const system = (reference?.scoring_systems || []).find(
				(s) => s.system_id === prescription.scoring_system_id,
			);
			setLoaded({
				zones: system?.zones || [],
				penalties: system?.penalties || [],
			});
			// Il foglio deve essere nel DOM prima della stampa: un frame basta.
			requestAnimationFrame(() => requestAnimationFrame(printSheet));
		} finally {
			setLoading(false);
		}
	};

	if (!hasStage && !hasSheet) return null;

	return (
		<div className="flex flex-wrap gap-1.5">
			{hasSheet && (
				<Button
					type="button"
					variant="outline"
					size="sm"
					className="rounded-sm h-7 text-xs"
					onClick={() => print("sheet")}
					disabled={loading}
				>
					{loading && mode === "sheet" ? (
						<Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
					) : (
						<Printer className="w-3.5 h-3.5 mr-1.5" />
					)}
					{label("pbShootPrintSheet", "Stampa scheda")}
				</Button>
			)}
			{hasStage && (
				<Button
					type="button"
					variant="outline"
					size="sm"
					className="rounded-sm h-7 text-xs"
					onClick={() => print("stage")}
					disabled={loading}
				>
					{loading && mode === "stage" ? (
						<Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
					) : (
						<Printer className="w-3.5 h-3.5 mr-1.5" />
					)}
					{label("pbShootPrintStage", "Stampa piantina")}
				</Button>
			)}
			{loaded && (
				<StagePrintSheet
					name={name}
					prescription={prescription}
					zones={loaded.zones}
					penalties={loaded.penalties}
					mode={mode}
				/>
			)}
		</div>
	);
}
